import React, { useState } from 'react';
import { logSleep, logSteps, logScreenTime, logMood, logDiary } from '~/lib/api.ts';

interface LogScreenProps {
  onBack: () => void;
  onDataLogged: () => void;
}

export const LogScreen: React.FC<LogScreenProps> = ({ onBack, onDataLogged }) => {
  const [activeTab, setActiveTab] = useState<'sleep' | 'steps' | 'screen' | 'mood' | 'diary'>('sleep');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  // Form states
  const [sleepHours, setSleepHours] = useState('');
  const [steps, setSteps] = useState('');
  const [screenTime, setScreenTime] = useState('');
  const [mood, setMood] = useState<number | null>(null);
  const [diaryText, setDiaryText] = useState('');

  const handleSleepPreset = async (hours: number) => {
    setLoading(true);
    try {
      await logSleep(hours);
      setMessage('✅ Сон записан!');
      onDataLogged();
    } catch (error) {
      setMessage('❌ Ошибка при записи сна');
    } finally {
      setLoading(false);
    }
  };

  const handleSleepCustom = async () => {
    const hours = parseFloat(sleepHours);
    if (isNaN(hours) || hours < 0 || hours > 24) {
      setMessage('❌ Введите корректное количество часов (0-24)');
      return;
    }
    
    setLoading(true);
    try {
      await logSleep(hours);
      setMessage('✅ Сон записан!');
      setSleepHours('');
      onDataLogged();
    } catch (error) {
      setMessage('❌ Ошибка при записи сна');
    } finally {
      setLoading(false);
    }
  };

  const handleStepsPreset = async (stepCount: number) => {
    setLoading(true);
    try {
      await logSteps(stepCount);
      setMessage('✅ Шаги записаны!');
      onDataLogged();
    } catch (error) {
      setMessage('❌ Ошибка при записи шагов');
    } finally {
      setLoading(false);
    }
  };

  const handleStepsCustom = async () => {
    const stepCount = parseInt(steps);
    if (isNaN(stepCount) || stepCount < 0 || stepCount > 100000) {
      setMessage('❌ Введите корректное количество шагов (0-100,000)');
      return;
    }
    
    setLoading(true);
    try {
      await logSteps(stepCount);
      setMessage('✅ Шаги записаны!');
      setSteps('');
      onDataLogged();
    } catch (error) {
      setMessage('❌ Ошибка при записи шагов');
    } finally {
      setLoading(false);
    }
  };

  const handleScreenPreset = async (hours: number) => {
    setLoading(true);
    try {
      await logScreenTime(hours * 60); // Convert to minutes
      setMessage('✅ Экранное время записано!');
      onDataLogged();
    } catch (error) {
      setMessage('❌ Ошибка при записи экранного времени');
    } finally {
      setLoading(false);
    }
  };

  const handleScreenCustom = async () => {
    const hours = parseFloat(screenTime);
    if (isNaN(hours) || hours < 0 || hours > 24) {
      setMessage('❌ Введите корректное количество часов (0-24)');
      return;
    }
    
    setLoading(true);
    try {
      await logScreenTime(hours * 60); // Convert to minutes
      setMessage('✅ Экранное время записано!');
      setScreenTime('');
      onDataLogged();
    } catch (error) {
      setMessage('❌ Ошибка при записи экранного времени');
    } finally {
      setLoading(false);
    }
  };

  const handleMoodSelect = async (score: number) => {
    setLoading(true);
    try {
      await logMood(score);
      setMessage('✅ Настроение записано!');
      onDataLogged();
    } catch (error) {
      setMessage('❌ Ошибка при записи настроения');
    } finally {
      setLoading(false);
    }
  };

  const handleDiarySubmit = async () => {
    if (!diaryText.trim()) {
      setMessage('❌ Введите текст записи');
      return;
    }
    
    setLoading(true);
    try {
      await logDiary(diaryText);
      setMessage('✅ Запись сохранена!');
      setDiaryText('');
      onDataLogged();
    } catch (error) {
      setMessage('❌ Ошибка при сохранении записи');
    } finally {
      setLoading(false);
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'sleep':
        return (
          <div className="log-tab-content">
            <h3>😴 Записать сон</h3>
            <div className="preset-buttons">
              <button onClick={() => handleSleepPreset(6)} disabled={loading}>6ч</button>
              <button onClick={() => handleSleepPreset(7)} disabled={loading}>7ч</button>
              <button onClick={() => handleSleepPreset(8)} disabled={loading}>8ч</button>
              <button onClick={() => handleSleepPreset(9)} disabled={loading}>9ч</button>
            </div>
            <div className="custom-input">
              <input
                type="number"
                placeholder="Точное время (часы)"
                value={sleepHours}
                onChange={(e) => setSleepHours(e.target.value)}
                min="0"
                max="24"
                step="0.5"
              />
              <button onClick={handleSleepCustom} disabled={loading}>
                Записать
              </button>
            </div>
          </div>
        );

      case 'steps':
        return (
          <div className="log-tab-content">
            <h3>👟 Записать шаги</h3>
            <div className="preset-buttons">
              <button onClick={() => handleStepsPreset(5000)} disabled={loading}>5-6к</button>
              <button onClick={() => handleStepsPreset(7000)} disabled={loading}>7-8к</button>
              <button onClick={() => handleStepsPreset(9000)} disabled={loading}>9-10к</button>
              <button onClick={() => handleStepsPreset(11000)} disabled={loading}>11-12к</button>
            </div>
            <div className="custom-input">
              <input
                type="number"
                placeholder="Точное количество"
                value={steps}
                onChange={(e) => setSteps(e.target.value)}
                min="0"
                max="100000"
              />
              <button onClick={handleStepsCustom} disabled={loading}>
                Записать
              </button>
            </div>
          </div>
        );

      case 'screen':
        return (
          <div className="log-tab-content">
            <h3>📱 Записать экранное время</h3>
            <div className="preset-buttons">
              <button onClick={() => handleScreenPreset(1)} disabled={loading}>1ч</button>
              <button onClick={() => handleScreenPreset(2)} disabled={loading}>2ч</button>
              <button onClick={() => handleScreenPreset(3)} disabled={loading}>3ч</button>
              <button onClick={() => handleScreenPreset(4)} disabled={loading}>4ч</button>
            </div>
            <div className="custom-input">
              <input
                type="number"
                placeholder="Точное время (часы)"
                value={screenTime}
                onChange={(e) => setScreenTime(e.target.value)}
                min="0"
                max="24"
                step="0.5"
              />
              <button onClick={handleScreenCustom} disabled={loading}>
                Записать
              </button>
            </div>
          </div>
        );

      case 'mood':
        return (
          <div className="log-tab-content">
            <h3>😊 Записать настроение</h3>
            <div className="mood-buttons">
              <button onClick={() => handleMoodSelect(1)} disabled={loading}>😢 1</button>
              <button onClick={() => handleMoodSelect(2)} disabled={loading}>😕 2</button>
              <button onClick={() => handleMoodSelect(3)} disabled={loading}>😐 3</button>
              <button onClick={() => handleMoodSelect(4)} disabled={loading}>😊 4</button>
              <button onClick={() => handleMoodSelect(5)} disabled={loading}>😄 5</button>
            </div>
          </div>
        );

      case 'diary':
        return (
          <div className="log-tab-content">
            <h3>📝 Дневник</h3>
            <textarea
              placeholder="Как прошел твой день?"
              value={diaryText}
              onChange={(e) => setDiaryText(e.target.value)}
              rows={4}
              className="diary-textarea"
            />
            <button 
              onClick={handleDiarySubmit} 
              disabled={loading || !diaryText.trim()}
              className="diary-submit"
            >
              {loading ? 'Сохранение...' : 'Сохранить'}
            </button>
          </div>
        );
    }
  };

  return (
    <div className="screen">
      <div className="header">
        <button className="back-button" onClick={onBack}>
          ← Назад
        </button>
        <h1>📝 Записать данные</h1>
      </div>

      <div className="log-tabs">
        <button 
          className={`log-tab ${activeTab === 'sleep' ? 'active' : ''}`}
          onClick={() => setActiveTab('sleep')}
        >
          😴 Сон
        </button>
        <button 
          className={`log-tab ${activeTab === 'steps' ? 'active' : ''}`}
          onClick={() => setActiveTab('steps')}
        >
          👟 Шаги
        </button>
        <button 
          className={`log-tab ${activeTab === 'screen' ? 'active' : ''}`}
          onClick={() => setActiveTab('screen')}
        >
          📱 Экран
        </button>
        <button 
          className={`log-tab ${activeTab === 'mood' ? 'active' : ''}`}
          onClick={() => setActiveTab('mood')}
        >
          😊 Настроение
        </button>
        <button 
          className={`log-tab ${activeTab === 'diary' ? 'active' : ''}`}
          onClick={() => setActiveTab('diary')}
        >
          📝 Дневник
        </button>
      </div>

      {renderTabContent()}

      {message && (
        <div className={`message ${message.includes('✅') ? 'success' : 'error'}`}>
          {message}
        </div>
      )}
    </div>
  );
};
